import React, { useEffect, useState } from "react";

import { Column, Table, AutoSizer, SortDirection } from "react-virtualized";

import { toast } from "react-toastify";

import * as loginReducer from "containers/Login/reducer";

import * as adminReducer from "containers/Admin/reducer";
import * as foldersReducer from "containers/Folders/reducer";

import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

import { useDispatch, useSelector } from "types/store";

import { Button } from "aible";

import SearchInput from "components/Inputs/SearchInput/SearchInput";

import GearIconPurple from "images/gear-purple.svg";

import styles from "./style.module.css";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { isEnabled } from "@/utils/featureFlags";
import { LineInput } from "components/Inputs/LineInput";
import CreateUsersModal from "components/Admin/CreateUsersModal";

const UserManagementView = ({} = {}) => {
  const dispatch = useDispatch();

  const activeTenant = useSelector((state) => loginReducer.getSelectedTenant(state));

  useEffect(() => {
    dispatch(adminReducer.updateUsersCall(activeTenant));
  }, []);

  const [showEditUserModal, updateShowEditUserModal] = useState(false);
  const [showAddUserModal, updateShowAddUserModal] = useState(false);
  const [showCreateUsersModal, updateShowCreateUsersModal] = useState(false);
  const [selectedUser, updateSelectedUser] = useState(undefined);
  const [searchFilter, updateSearchFilter] = useState("");

  const users = useSelector((state) => adminReducer.getUsers(state));

  const isLoadingUsers = useSelector((state) => adminReducer.getIsLoadingUsers(state));

  const folders = useSelector((state) => foldersReducer.getFolders(state));

  const activeUsers = users.filter((user) => user?.status !== "DELETED");

  const userInfo = activeUsers?.map((user) => {
    const ownedFolders = Object.values(folders)?.filter((folder) => folder?.created_by?.id === user?.id);

    const userRole =
      user?.type === "USER"
        ? "User"
        : user?.type === "ADMIN"
          ? "Admin"
          : user?.type === "PENDING_USER"
            ? "Pending User"
            : undefined;

    const createdDate = new Date(Date.parse(user?.created_date));

    const formattedDate = createdDate.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

    return {
      userName: user?.fullname,
      email: user?.email,
      folders: user?.id && ownedFolders?.length,
      status:
        user?.tags && user?.tags !== "NORMAL_USER"
          ? user.tags
          : user?.type === "USER"
            ? "User"
            : user?.type === "PENDING_USER"
              ? "Pending User"
              : undefined,
      id: user?.id,
      created_date: user?.created_date && formattedDate
    };
  });

  const columns = [
    { label: "Username", dataKey: "userName", width: 300 },
    { label: "Email", dataKey: "email", width: 300 },
    { label: "Status", dataKey: "status", width: 300 },
    { label: "Folders", dataKey: "folders", width: 100 },
    { label: "Creation Date", dataKey: "created_date", width: 200 }
  ];

  return (
    <React.Fragment>
      <div className={styles.userManagementPanel}>
        {isLoadingUsers ? (
          <LoadingIndicator
            style={{
              width: "20px",
              height: "18px",
              transform: "scale(0.40)",
              margin: "10px 0px 3px 0px"
            }}
          />
        ) : (
          <React.Fragment>
            <div className={styles.userManagementHeader}>
              <div className={styles.userManagementTitle}>{`${userInfo?.length} Members`}</div>
              <div className={styles.userManagementActions}>
                <SearchInput
                  value={searchFilter}
                  onChange={(val) => updateSearchFilter(val)}
                  style={{ height: "37px" }}
                />
                {isEnabled("spark-agents-templates-view") ? (
                  <Button
                    onClick={() => {
                      updateShowCreateUsersModal(true);
                    }}
                    style={{ marginRight: "10px" }}
                    type="primary"
                  >
                    {"Create users"}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      updateShowAddUserModal(true);
                    }}
                    type="primary"
                  >
                    {"Add Users"}
                  </Button>
                )}
              </div>
            </div>
            <div className={styles.userManagementTableContainer}>
              <UserManagementTable
                data={userInfo}
                columns={columns}
                updateShowEditUserModal={updateShowEditUserModal}
                updateSelectedUser={updateSelectedUser}
                searchFilter={searchFilter}
              />
            </div>
          </React.Fragment>
        )}
      </div>
      <EditUserModal showModal={showEditUserModal} updateShowModal={updateShowEditUserModal} user={selectedUser} />
      <AddUserModal showModal={showAddUserModal} updateShowModal={updateShowAddUserModal} />
      <CreateUsersModal showModal={showCreateUsersModal} updateShowModal={updateShowCreateUsersModal} />
    </React.Fragment>
  );
};

const UserManagementTable = ({ data, columns, updateShowEditUserModal, updateSelectedUser, searchFilter } = {}) => {
  const [sortBy, updateSortBy] = useState(undefined);
  const [sortDirection, updateSortDirection] = useState(SortDirection.ASC);
  const [sortedData, updateSortedData] = useState(data);

  const compareStrings = (a, b) => {
    return a.localeCompare(b);
  };

  const compareNumbers = (a, b) => {
    return a < b ? -1 : a > b ? 1 : 0;
  };

  const compareDates = (a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateA < dateB ? -1 : 1;
  };

  const sort = ({ sortBy, sortDirection }) => {
    const sortedArray = [...data].sort((a, b) => {
      const aValue = Array.isArray(a[sortBy]) ? a[sortBy][0] : a[sortBy];
      const bValue = Array.isArray(b[sortBy]) ? b[sortBy][0] : b[sortBy];

      if (aValue === undefined) return sortDirection === SortDirection.ASC ? 1 : -1;
      if (bValue === undefined) return sortDirection === SortDirection.ASC ? -1 : 1;

      if (sortBy === "folders") {
        return sortDirection === SortDirection.ASC ? compareNumbers(bValue, aValue) : compareNumbers(aValue, bValue);
      }

      if (sortBy === "status") {
        return sortDirection === SortDirection.ASC ? compareStrings(bValue, aValue) : compareStrings(aValue, bValue);
      }

      if (sortBy === "created_date") {
        return sortDirection === SortDirection.ASC ? compareDates(aValue, bValue) : compareDates(bValue, aValue);
      }

      return sortDirection === SortDirection.ASC ? compareStrings(aValue, bValue) : compareStrings(bValue, aValue);
    });

    updateSortBy(sortBy);
    updateSortDirection(sortDirection);
    return updateSortedData(sortedArray ?? []);
  };

  useEffect(() => {
    sort({ sortBy: "userName", sortDirection: SortDirection.ASC });
  }, []);

  const filteredData = sortedData.filter((item) =>
    searchFilter === ""
      ? item
      : item?.userName?.toLowerCase()?.startsWith(searchFilter?.toLowerCase()) ||
        item?.email?.toLowerCase()?.startsWith(searchFilter?.toLowerCase())
  );

  return (
    <AutoSizer>
      {({ width, height }) => (
        <Table
          width={width}
          height={height}
          headerHeight={30}
          rowHeight={44}
          rowCount={filteredData?.length}
          rowGetter={({ index }) => filteredData[index]}
          sort={sort}
          sortBy={sortBy}
          sortDirection={sortDirection}
          rowClassName={styles.userManagementRow}
          className={styles.userManagementTable}
          headerClassName={styles.userManagementHeaderRow}
        >
          {columns.map((column, index) => (
            <Column
              key={index}
              label={column.label}
              dataKey={column.dataKey}
              width={column.width}
              flexGrow={column.flexGrow}
              cellClassName={styles.userManagementItem}
            />
          ))}
          <Column
            key="id"
            label=""
            width={100}
            dataKey="id"
            cellRenderer={({ rowData }) => (
              <Button
                isSmall
                onClick={() => {
                  updateSelectedUser(rowData);
                  updateShowEditUserModal(true);
                }}
                style={{ padding: "5px 7px" }}
              >
                <img src={GearIconPurple} />
              </Button>
            )}
            cellClassName={styles.userManagementSettings}
          />
        </Table>
      )}
    </AutoSizer>
  );
};

const EditUserModal = ({ showModal, updateShowModal, user } = {}) => {
  const dispatch = useDispatch();

  const [editModalView, updateEditModalView] = useState("EDIT");

  const resetModals = () => {
    updateShowModal(false);
    updateEditModalView("EDIT");
  };

  return (
    <React.Fragment>
      <Dialog open={showModal} onOpenChange={() => resetModals()}>
        {editModalView === "DELETE" ? (
          <DeleteUserModalContent
            updateShowModal={updateShowModal}
            user={user}
            updateEditModalView={updateEditModalView}
          />
        ) : editModalView === "UPDATE_PASSWORD" &&
          (isEnabled("update-current-user-password") || isEnabled("admin-force-update-password")) ? (
          <UpdatePasswordModalContent
            updateShowModal={updateShowModal}
            user={user}
            updateEditModalView={updateEditModalView}
          />
        ) : (
          <EditUserModalContent user={user} updateEditModalView={updateEditModalView} resetModals={resetModals} />
        )}
      </Dialog>
    </React.Fragment>
  );
};

const EditUserModalContent = ({ user, updateEditModalView, resetModals }) => {
  const dispatch = useDispatch();
  const isPendingUser = user?.status === "Pending User" || user?.status?.includes("ACTIVATION_EMAIL_SENT");
  const currentUserEmail = useSelector((state) => loginReducer.getUserEmail(state));
  const isCurrentUser = currentUserEmail === user?.email;

  const resetUserPassword = (email) => dispatch(adminReducer.resetUserPassword(email));

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit User</DialogTitle>
      </DialogHeader>
      <div className={styles.userModalBody}>
        <div className={styles.editUserItem}>
          <div className={styles.editUserItemLabel}>{"Fullname: "}</div>
          <div className={styles.editUserItemField}>{user?.userName}</div>
        </div>
        <div className={styles.editUserItem}>
          <div className={styles.editUserItemLabel}>{"Email: "}</div>
          <div className={styles.editUserItemField}>{user?.email}</div>
        </div>
        <div className={styles.editUserItem}>
          <div className={styles.editUserItemLabel}>{"Password: "}</div>
          <div className={styles.editUserItemField}>
            {((isCurrentUser && isEnabled("update-current-user-password")) ||
              (!isPendingUser && isEnabled("admin-force-update-password"))) && (
              <Button
                isSmall
                style={{ marginRight: "8px" }}
                onClick={() => {
                  updateEditModalView("UPDATE_PASSWORD");
                }}
              >
                {"Update Password"}
              </Button>
            )}
            <Button
              isSmall
              onClick={() => {
                if (user.email && !isPendingUser) {
                  resetUserPassword(user.email);
                }
              }}
              disabled={isPendingUser}
            >
              {"Send Password Reset Link"}
            </Button>
          </div>
        </div>
        <Button
          isSmall
          style={{ color: "red", borderColor: "red" }}
          onClick={() => {
            console.log("Delete user was clicked");
            updateEditModalView("DELETE");
          }}
        >
          {"Delete User"}
        </Button>
      </div>
      <DialogFooter>
        <Button onClick={() => resetModals()}>Close</Button>
      </DialogFooter>
    </DialogContent>
  );
};

const DeleteUserModalContent = ({ updateShowModal, user, updateEditModalView } = {}) => {
  const dispatch = useDispatch();

  const activeTenant = useSelector((state) => loginReducer.getSelectedTenant(state));

  const handleDeleteUser = (email) => {
    console.log(`Deleting user from tenant id: ${activeTenant?.id}`);
    dispatch(adminReducer.removeUserFromTenant(activeTenant?.id, email));
    updateEditModalView("EDIT");
    updateShowModal(false);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className={styles.userModalTitle}>{"Confirm User Deletion"}</DialogTitle>
      </DialogHeader>
      <div className={styles.userModalBody}>
        {"Are you sure you would like to delete this user? This action cannot be undone."}
        <div className={styles.addUserErrorText}>{user?.email}</div>
        {user?.folders !== null && (
          <div className={styles.addUserErrorText}>{`User owns ${user?.folders} folders.`}</div>
        )}
      </div>
      <DialogFooter>
        <Button onClick={() => updateEditModalView("EDIT")}>Cancel</Button>
        <Button
          style={{ color: "white", borderColor: "red", backgroundColor: "red" }}
          onClick={() => {
            console.log("Confirmed deleting user");
            handleDeleteUser(user?.email);
          }}
        >
          DELETE USER
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const UpdatePasswordModalContent = ({ updateShowModal, user, updateEditModalView } = {}) => {
  const dispatch = useDispatch();

  const currentUserEmail = useSelector((state) => loginReducer.getUserEmail(state));
  const isCurrentUser = currentUserEmail === user?.email;
  const isPendingUser = user?.status === "Pending User" || user?.status?.includes("ACTIVATION_EMAIL_SENT");

  const [currentPassword, updateCurrentPassword] = useState("");
  const [newPassword, updateNewPassword] = useState("");
  const [confirmPassword, updateConfirmPassword] = useState("");

  const [isLoading, updateIsLoading] = useState(false);

  const canChangePassword =
    (isCurrentUser && isEnabled("update-current-user-password")) ||
    (!isPendingUser && isEnabled("admin-force-update-password"));

  const isValidCurrentUserPassword =
    (isCurrentUser && currentPassword?.length > 0) || (!isCurrentUser && isEnabled("admin-force-update-password"));

  const isValidNewPassword = newPassword === confirmPassword && newPassword?.length > 0;

  const isValidPasswordSettings = canChangePassword && isValidCurrentUserPassword && isValidNewPassword;

  const showNewPasswordMismatch = newPassword?.length > 0 && confirmPassword?.length > 0 && !isValidNewPassword;
  const showCurrentPasswordWarning = isValidNewPassword && isCurrentUser && !currentPassword?.length > 0;

  const getErrorText = () => {
    if (showNewPasswordMismatch) {
      return "New passwords must match.";
    }
    if (showCurrentPasswordWarning) {
      return "Enter your current password.";
    }
    return undefined;
  };

  const handleUpdatePassword = async () => {
    updateIsLoading(true);
    if (!isValidPasswordSettings) {
      return;
    }
    try {
      await dispatch(adminReducer.updateUserPassword(newPassword, user, currentPassword));
      updateEditModalView("EDIT");
      updateShowModal(false);
      toast(`Password has been updated!`, {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
    } catch (e) {
      console.log(e);
      updateNewPassword("");
      updateConfirmPassword("");
    } finally {
      updateIsLoading(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className={styles.userModalTitle}>{"Update Password"}</DialogTitle>
      </DialogHeader>
      {isLoading ? (
        <div className={styles.userModalBody}>
          <LoadingIndicator />
        </div>
      ) : (
        <div className={styles.userModalBody}>
          {!isCurrentUser && "You are signed in as an Admin user and can change passwords for any user."}
          <div>
            {"Choose a new password for "} <span className={"font-bold"}>{user?.email}</span>
          </div>
          <div style={{ minHeight: "147px" }}>
            {isCurrentUser && (
              <div className={styles.editUserItem}>
                <div className={styles.editUserItemLabel}>{"Current Password: "}</div>
                <div className={styles.editUserItemField}>
                  <LineInput
                    value={currentPassword}
                    onChange={(val) => updateCurrentPassword(val)}
                    isPassword={true}
                    updateInstant
                  />
                </div>
              </div>
            )}
            <div className={styles.editUserItem}>
              <div className={styles.editUserItemLabel}>{"New Password: "}</div>
              <div className={styles.editUserItemField}>
                <LineInput
                  value={newPassword}
                  onChange={(val) => updateNewPassword(val)}
                  isPassword={true}
                  updateInstant
                />
              </div>
            </div>
            <div className={styles.editUserItem}>
              <div className={styles.editUserItemLabel}>{"Confirm Password: "}</div>
              <div className={styles.editUserItemField}>
                <LineInput
                  value={confirmPassword}
                  onChange={(val) => updateConfirmPassword(val)}
                  isPassword={true}
                  updateInstant
                />
              </div>
            </div>
            {getErrorText()?.length > 0 && <div className={styles.addUserErrorText}>{getErrorText()}</div>}
          </div>
        </div>
      )}
      <DialogFooter>
        <Button onClick={() => updateEditModalView("EDIT")}>Cancel</Button>
        <Button
          type={"primary"}
          onClick={() => {
            handleUpdatePassword();
          }}
          disabled={!isValidPasswordSettings || isLoading}
        >
          Update Password
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const AddUserModal = ({ showModal, updateShowModal } = {}) => {
  const dispatch = useDispatch();
  const [userEmails, updateUserEmails] = useState(undefined);
  const [emailsArray, updateEmailsArray] = useState(undefined);
  const [isLoading, updateIsLoading] = useState(false);
  const [errorsArray, updateErrorsArray] = useState(undefined);
  const [allEmailsValid, updateAllEmailsValid] = useState(true);

  const isLoadingUsers = useSelector((state) => adminReducer.getIsLoadingUsers(state));
  const activeTenant = useSelector((state) => loginReducer.getSelectedTenant(state));

  const resetFields = () => {
    updateUserEmails(undefined);
    updateEmailsArray(undefined);
    updateAllEmailsValid(true);
  };

  const handleAddUsers = async () => {
    updateIsLoading(true);
    updateErrorsArray(undefined);
    const errors = await dispatch(adminReducer.inviteMultipleFromTenant(activeTenant?.id, emailsArray));
    updateIsLoading(false);
    if (errors?.length > 0) {
      updateErrorsArray(errors);
    } else {
      toast("All users have been invited!", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
      updateShowModal(false);
      resetFields();
    }
  };

  const parseEmails = (emails) => {
    const parsedEmails = emails
      .replace(/[\s,]+/g, " ")
      .trim()
      .split(" ")
      .filter((email) => email !== "");
    if (checkAllValidEmails(parsedEmails)) {
      updateAllEmailsValid(true);
    } else {
      updateAllEmailsValid(false);
    }
    return parsedEmails;
  };

  const checkValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const checkAllValidEmails = (emailsArray) => {
    return emailsArray.every(checkValidEmail);
  };

  return (
    <Dialog open={showModal} onOpenChange={() => updateShowModal(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{"Add Users"}</DialogTitle>
        </DialogHeader>
        <div className={styles.userModalBody}>
          <div className={styles.editUserItem}>
            <div className={styles.addUserItemLabel}>{"Email addresses"}</div>
          </div>
          <div className={styles.editUserItem}>
            <textarea
              className={styles.userEmailsField}
              value={userEmails}
              onChange={(val) => {
                updateUserEmails(val.target.value);
                const parsedEmails = parseEmails(val.target.value);
                updateEmailsArray(parsedEmails);
                if (errorsArray && errorsArray?.length > 0) {
                  updateErrorsArray(undefined);
                }
              }}
              placeholder={"Separate emails by comma or new line."}
            />
          </div>
          <div className={styles.addUserErrorContainer}>
            {!allEmailsValid && (
              <div className={styles.addUserErrorText}>{"Please check that all emails are valid."}</div>
            )}
            {errorsArray && (
              <div className={styles.addUserErrorText}>
                {"The follow users could not be added. This may be due to the emails already existing in this account."}
              </div>
            )}
            {errorsArray &&
              errorsArray.map((error, index) => {
                return <div key={error + "_" + index}>{error}</div>;
              })}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => {
              updateShowModal(false);
              resetFields();
              updateErrorsArray(undefined);
            }}
          >
            {"Close"}
          </Button>
          {!isLoadingUsers && (
            <Button
              type="primary"
              onClick={() => handleAddUsers()}
              disabled={!(emailsArray && emailsArray?.length > 0 && allEmailsValid)}
              isLoading={isLoading}
            >
              {"Add Users"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagementView;
